import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import {
  DEFAULT_DATE_FORMAT,
  formatDate,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { TicketRelatedResourceType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/enums/ticket-related-resource-type.enum';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import i18n from '@app/core/locale/i18n';

import { normalizeLineBreaks } from '../../../shared/normalize-line-breaks/normalize-line-breaks';
import { AddMessageComponent } from '../../../ticket-update/add-message.component';
import { useTicketContext } from '../../../ticket.context';
import { TicketMessageComponent } from '../../ticket-message.component';
import { AssigneeCardComponent } from './assignee-card.component';
import { ChargePointCardComponent } from './charge-point-card.component';
import * as css from './information-tab.module.scss';
import { RelatedResourceComponent } from './related-resource.component';

export const InformationTabComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const ticket = useTicketContext();

  const orderedMessages = useMemo(
    () =>
      (ticket.message ?? []).toSorted(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [ticket.message],
  );

  const chargePointResource = ticket.relatedResources.find(
    (resource) => resource.type === TicketRelatedResourceType.ChargePoint,
  );
  const otherResources = ticket.relatedResources.filter(
    (resource) => resource.type !== TicketRelatedResourceType.ChargePoint,
  );

  return (
    <Stack direction="column" gap={GapEnum.XL}>
      <Stack direction="row" justifyContent="space-between">
        <Text color={TextColor.Secondary} fontSize={TextFontSize.Medium}>
          {t('$.ticket-details.created-at', {
            date: formatDate(ticket.createdAt, i18n, DEFAULT_DATE_FORMAT),
            time: formatDate(ticket.createdAt, i18n, 'HH:mm'),
            creator:
              `${ticket.creator?.firstName ?? ''} ${ticket.creator?.lastName ?? ''}`.trim(),
          })}
        </Text>
        <Text color={TextColor.Secondary} fontSize={TextFontSize.Medium}>
          {t('$.ticket-details.updated-at', {
            date: formatDate(ticket.updatedAt, i18n, DEFAULT_DATE_FORMAT),
            time: formatDate(ticket.updatedAt, i18n, 'HH:mm'),
          })}
        </Text>
      </Stack>
      <Stack direction="row" gap={GapEnum.L} alignItems="stretch">
        <Card gap={GapEnum.M} flex={2}>
          <h3>{t('delmonicos-shared-sdk.outputs-ticket.description')}</h3>
          <span className={css.description}>
            {normalizeLineBreaks(ticket.description)}
          </span>
        </Card>
        <Stack direction="column" gap={GapEnum.L} flex={1}>
          {chargePointResource && (
            <ChargePointCardComponent chargePointId={chargePointResource.id} />
          )}
          {otherResources.map((resource) => (
            <RelatedResourceComponent key={resource.id} resource={resource} />
          ))}
          <AssigneeCardComponent assignee={ticket.assignee} />
        </Stack>
      </Stack>
      <Stack direction="column" gap={GapEnum.M}>
        <h3>
          {t('delmonicos-shared-sdk.outputs-ticket.notification-contacts')}
        </h3>
        <Stack direction="column" gap={GapEnum.S}>
          {ticket.notificationContacts.map((contact) => (
            <Link to={`mailto:${contact.contact}`} key={contact.contact}>
              {contact.contact}
            </Link>
          ))}
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="column" gap={GapEnum.M}>
        <AddMessageComponent id={ticket.id} />
        {orderedMessages.map((message) => (
          <TicketMessageComponent
            key={message.createdAt.toString()}
            message={message}
          />
        ))}
      </Stack>
    </Stack>
  );
};
